import React, { useState, useEffect } from 'react';
import style from './ReportsHeader.module.scss';
import ReportsFilters from './../ReportsFilters';
import ParserSettings from './../ParserSettings';
import { useQuery } from '@apollo/client'
import {
  Button, Typography, Popconfirm, Tooltip, Badge } from 'antd';
import { FilterOutlined, DeleteOutlined, FileTextOutlined, MailOutlined, CheckSquareOutlined, SettingOutlined, FileExcelOutlined,
} from '@ant-design/icons';
import { SelectValue } from 'antd/lib/select';
import { GET_SELECTED } from './../../../graphql/local/queries/reports'
import CampaignSelect from './CampaignSelect';

const { Title } = Typography;

type Props = {
  totalCount: number;
  handleDelete: () => void;
  handleExport: () => void;
  handleNotExportedExport: () => void;
  handleSelectingReports: () => void;
  handleSendMail: (woodpeckerCampaignId: number) => void;
};

const ReportsHeader: React.FC<Props> = ({
  totalCount,
  handleDelete,
  handleExport,
  handleNotExportedExport,
  handleSendMail,
  handleSelectingReports,
}) => {
  const { data: fetchedSelected } = useQuery(GET_SELECTED)
  const [ hasSelected, setHasSelected ] = useState(false)
  const [ filtersVisible, setFiltersVisible ] = useState(false);
  const [ settingsVisible, setSettingsVisible ] = useState(false);
  const [ selectedCampaign, setSelectedCampaign ] = useState<number>();

  useEffect(()=> {
    setHasSelected(fetchedSelected?.selected?.length > 0)
  }, [ fetchedSelected ])

  const onConfirmDelete = (): void => {
    handleDelete();
  };
  const onSendMail = (): void => {
    handleSendMail(selectedCampaign as number)
  }
  const onChange = (value: SelectValue): void => {
    setSelectedCampaign(value as number)
  }

  const getMailPopconfirm = (): JSX.Element => (<>
    <h4>Woodpecker Campaign</h4>
    <CampaignSelect onChange={ onChange } setLoading={ null }/>
  </>
  )

  return (
    <div className={ style.header }>
      <div className={ style.leftContainer }>
        <Tooltip
          placement="topLeft"
          autoAdjustOverflow={ true }
          mouseEnterDelay={ .6 }
          overlayClassName={ style.antTooltip }
          title="Delete">
          <Popconfirm
            disabled={ !hasSelected }
            title={ 'Delete ' + fetchedSelected?.selected?.length +(fetchedSelected?.selected?.length === 1 ? ' row?' : ' rows?' ) }
            onConfirm={ onConfirmDelete }
            okText="Delete"
            cancelText="Cancel"
            placement="bottomLeft"
            icon={ <DeleteOutlined /> }>
            <Button type="primary" danger disabled={ !hasSelected }>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </Tooltip>
        <Tooltip
          placement="topLeft"
          autoAdjustOverflow={ true }
          mouseEnterDelay={ .6 }
          overlayClassName={ style.antTooltip }
          title="Export to csv">
          <Button type="primary" disabled={ !hasSelected } onClick={ handleExport }>
            <FileTextOutlined/>
          </Button>
        </Tooltip>
        <Tooltip
          placement="topLeft"
          autoAdjustOverflow={ true }
          mouseEnterDelay={ .6 }
          overlayClassName={ style.antTooltip }
          title="Export to csv reports that are not exported yet">
          <Button type="primary" onClick={ handleNotExportedExport }>
            <FileExcelOutlined/>
          </Button>
        </Tooltip>
        <Tooltip
          placement="topLeft"
          autoAdjustOverflow={ true }
          mouseEnterDelay={ .6 }
          overlayClassName={ style.antTooltip }
          title="Send mail">
          <Popconfirm
            disabled={ !hasSelected }
            okText="Send"
            cancelText="Cancel"
            placement="bottomLeft"
            icon={ <MailOutlined /> }
            onConfirm={ onSendMail }
            title={ getMailPopconfirm() }>
            <Button type="primary" disabled={ !hasSelected }>
              <MailOutlined />
            </Button>
          </Popconfirm>
        </Tooltip>
        <Tooltip
          placement="topLeft"
          autoAdjustOverflow={ true }
          mouseEnterDelay={ .6 }
          overlayClassName={ style.antTooltip }
          title="Select available reports to send mail">
          <Button type="dashed" onClick={ handleSelectingReports }>
            <CheckSquareOutlined />
          </Button>
        </Tooltip>
      </div>

      <Title level={ 2 } className={ style.antTitle }>
        Reports
      </Title>
      <div className={ style.rightContainer }>
        <Badge count={ totalCount }
          offset={ [ -102, 36 ] }
          overflowCount={ 999 }
          showZero={ true }>
          <Button
            id="filters-button"
            type="primary"
            onClick={ (): void => setFiltersVisible((prev) => !prev) }>
            <FilterOutlined /> Filters
          </Button>
        </Badge>
        <Button
          id="settings-button"
          className={ style.settingsButton }
          type="primary"
          onClick={ (): void => setSettingsVisible(() => true) }>
          <SettingOutlined /> Settings
        </Button>
      </div>
      <ReportsFilters
        visible={ filtersVisible }
        handleVisible={ (): void => setFiltersVisible((prev) => !prev) } />
      <ParserSettings
        visible={ settingsVisible }
        setVisible={ setSettingsVisible }
      />
    </div>
  );
};

export default ReportsHeader;
